import React from "react";
import PropTypes from "prop-types";

// CSS
import "./index.scss";

/**
 * The rating a rate earns. Bands are the ones the scale lists: above 80 excellent,
 * 70 to 80 good, below 70 poor. With no rate, the model is either still being
 * generated or failed. Zero is a rate (poor), not a missing one.
 */
export function accuracyRating(value, status) {
	if (value != null) {
		if (value > 80) return "excellent";
		if (value >= 70) return "good";
		return "poor";
	}
	return status === "created" || status === "started" ? "pending" : "error";
}

const RATINGS = {
	excellent: { title: "Excellent", subtitle: "Acceptable for Use" },
	good: { title: "Good", subtitle: "Acceptable for Use" },
	poor: { title: "Poor", subtitle: "Do Not Recommend Using" },
	pending: { title: "Pending", subtitle: "Model Generation in Progress" },
	error: {
		title: "Model Generation Error",
		subtitle: "Please reach out to your ArcSpan representative.",
	},
};

const SCALE = [
	{ rating: "excellent", label: "Above 80" },
	{ rating: "good", label: "70 to 80" },
	{ rating: "poor", label: "Below 70" },
];

// Drawn in currentColor, so the rating's colour comes from the stylesheet's tokens.
function RatingIcon({ rating }) {
	const glyph = {
		excellent: <path d="M14 24.5l6.5 6.5L34 17.5" />,
		good: <path d="M14 24.5l6.5 6.5L34 17.5" />,
		poor: <path d="M17 17l14 14M31 17L17 31" />,
		pending: <path d="M24 14v10l6 4" />,
		error: <path d="M24 14v12M24 32v1" />,
	}[rating];
	return (
		<svg
			className="model-accuracy__icon"
			viewBox="0 0 48 48"
			width="56"
			height="56"
			aria-hidden="true"
		>
			<circle
				cx="24"
				cy="24"
				r="24"
				fill="currentColor"
			/>
			<g
				className="model-accuracy__glyph"
				fill="none"
				strokeWidth="4"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				{glyph}
			</g>
		</svg>
	);
}
RatingIcon.propTypes = { rating: PropTypes.string.isRequired };

/**
 * Model Accuracy Rate, the body of its BuilderCard (Option B, ASB-2995): the rate,
 * the rating it earns, and the scale it is read against. The card supplies the title.
 * `value` is a whole percent.
 */
function ModelAccuracyRate({ value, status }) {
	const rating = accuracyRating(value, status);
	const { title, subtitle } = RATINGS[rating];

	return (
		<div className="model-accuracy">
			{/* No rate, no placeholder: the Pending or Error box is the headline. */}
			{value != null && <p className="model-accuracy__rate">{value}%</p>}

			<div className={`model-accuracy__rating model-accuracy__rating--${rating}`}>
				<div>
					<p className="model-accuracy__title">{title}</p>
					<p className="model-accuracy__subtitle">{subtitle}</p>
				</div>
				<RatingIcon rating={rating} />
			</div>

			{/* A failed run is not read against the scale, so it is left out. */}
			{rating !== "error" && (
				<div className="model-accuracy__scale">
					<p className="model-accuracy__scale-title">Rating Scale</p>
					<ul>
						{SCALE.map((band) => (
							<li
								key={band.rating}
								className={[
									`model-accuracy__band model-accuracy__band--${band.rating}`,
									band.rating === rating ? "model-accuracy__band--active" : "",
								]
									.join(" ")
									.trim()}
							>
								<span
									className="model-accuracy__dot"
									aria-hidden="true"
								/>
								{band.label}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
}

ModelAccuracyRate.propTypes = {
	/** whole percent */
	value: PropTypes.number,
	status: PropTypes.string.isRequired,
};

export default ModelAccuracyRate;
